import { Checkbox, NumberInput, Select, Stack, TextInput } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { DateInput } from 'components/inputs/DateInput';
import { IFormInputSpec, IFormInputSpecConstant } from 'pages/revisions-module/template-editor/editors/form/types';
import { useEffect, useMemo } from 'react';

interface ConstantInputPropertiesSubFormProps {
  form: UseFormReturnType<IFormInputSpec>;
}

/**
 * Sub-form for configuring constant input properties.
 */
export default function ConstantInputPropertiesSubForm({ form }: ConstantInputPropertiesSubFormProps) {
  const constantType = useMemo(
    () => ((form.values as IFormInputSpecConstant).constantType ?? 'text') as IFormInputSpecConstant['constantType'],
    [form.values]
  );

  useEffect(() => {
    if ((form.values as IFormInputSpecConstant).constantType === undefined) {
      form.setFieldValue('constantType', 'text');
    }
  }, [form.values]);

  useEffect(() => {
    if (constantType === 'bool' && typeof (form.values as IFormInputSpecConstant).value !== 'boolean') {
      form.setFieldValue('value', false);
      return;
    }

    if (constantType === 'number' && typeof (form.values as IFormInputSpecConstant).value !== 'number') {
      form.setFieldValue('value', 0);
      return;
    }

    if (
      (constantType === 'text' || constantType === 'date') &&
      typeof (form.values as IFormInputSpecConstant).value !== 'string'
    ) {
      form.setFieldValue('value', '');
    }
  }, [constantType, form.values]);

  const valueProps = form.getInputProps('value');
  const constantTypeProps = form.getInputProps('constantType');

  return (
    <Stack spacing={12}>
      <Select
        size="sm"
        label="Typ hodnoty"
        required
        data={[
          { value: 'text', label: 'Text' },
          { value: 'number', label: 'Číslo' },
          { value: 'date', label: 'Dátum' },
          { value: 'bool', label: 'Pravdivostná hodnota' },
        ]}
        value={constantType}
        onChange={(value) => {
          valueProps.onChange(undefined); // reset value when type changes
          constantTypeProps.onChange(value);
        }}
      />

      {constantType === 'text' && (
        <TextInput size="sm" label="Hodnota" placeholder="Zadajte text" required {...valueProps} />
      )}

      {constantType === 'number' && (
        <NumberInput size="sm" label="Hodnota" placeholder="Zadajte číslo" required {...valueProps} />
      )}

      {constantType === 'date' && (
        <DateInput size="sm" label="Hodnota" placeholder="Vyberte dátum" required {...valueProps} />
      )}

      {constantType === 'bool' && (
        <Checkbox size="sm" label="Hodnota" {...form.getInputProps('value', { type: 'checkbox' })} />
      )}
    </Stack>
  );
}
